// Write to the agent (D82): the message, the CLI and its model, and whether to stream. Enter
// sends, Shift+Enter starts a new line; sending never waits for the agent, so messages queue.
import { PaperPlaneRight } from "@phosphor-icons/react";
import { type KeyboardEvent, useState } from "react";
import { Button, Switch, Textarea } from "rizzui";

import { useT } from "../i18n";
import { SelectField } from "../ui/SideDrawer";
import type { TurnBody } from "./api";
import { CLIS, isCli, modelsFor } from "./clis";
import { type ChatPrefs, loadPrefs, savePrefs } from "./prefs";

export function Composer({ onSend, queued }: { onSend: (text: string, body: TurnBody) => void; queued: number }) {
  const t = useT().chat.composer;
  const [text, setText] = useState("");
  const [prefs, setPrefs] = useState<ChatPrefs>(loadPrefs);
  const update = (change: Partial<ChatPrefs>) => {
    const next = { ...prefs, ...change };
    setPrefs(next);
    savePrefs(next);
  };
  const send = () => {
    const message = text.trim();
    if (!message) return;
    onSend(message, { cli: prefs.cli, model: prefs.model || null, stream: prefs.stream });
    setText("");
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };
  return (
    <form
      className="space-y-3 border-t border-muted pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <Textarea label={t.label} placeholder={t.placeholder} rows={3} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          className="w-40"
          label={t.cli}
          value={prefs.cli}
          options={CLIS.map((c) => ({ value: c.value, label: c.label }))}
          onChange={(v) => isCli(v) && update({ cli: v, model: "" })}
        />
        <SelectField className="w-56" label={t.model} value={prefs.model} options={modelsFor(prefs.cli, t.defaultModel)} onChange={(model) => update({ model })} />
        <div className="pb-2" title={t.streamHint}>
          <Switch role="switch" size="sm" label={t.stream} checked={prefs.stream} onChange={(e) => update({ stream: e.target.checked })} />
        </div>
        <Button type="submit" className="ms-auto gap-1.5" disabled={!text.trim()}>
          <PaperPlaneRight weight="bold" className="h-4 w-4" /> {t.send}
        </Button>
      </div>
      {prefs.cli === "cursor" ? <p className="m-0 text-[12.5px] text-orange-dark">{t.cursorSoon}</p> : null}
      {queued > 0 ? <p className="m-0 text-[12.5px] text-gray-500">{t.queued(queued)}</p> : null}
    </form>
  );
}
